/**
 * useMultiCart Hook
 *
 * TanStack Query hook for fetching multi-cart data.
 * Only fetches when user is authenticated.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@teletienda/shared-auth-ui';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { getMultiCart } from './api';
import type { MultiCartResponse } from './types';

/**
 * Query key for multi-cart data
 * Used for cache invalidation in useMultiCartSync
 */
export const MULTI_CART_QUERY_KEY = ['multi-cart'] as const;

/**
 * Options for useMultiCart hook
 */
export interface UseMultiCartOptions {
  /** Enable/disable the query (default: true when authenticated) */
  enabled?: boolean;
}

/**
 * Hook for fetching multi-cart data
 *
 * Fetches aggregated cart summaries from all merchants the user has interacted with.
 * Only enabled when user is authenticated.
 *
 * @param options - Hook options
 * @returns Query result with cart data, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * function CartIcon() {
 *   const { data, isLoading, error } = useMultiCart();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (error) return <ErrorIcon />;
 *
 *   return <Badge>{data?.totalItems ?? 0}</Badge>;
 * }
 * ```
 */
export function useMultiCart(options: UseMultiCartOptions = {}) {
  const { isAuthenticated, user } = useAuth();
  const { enabled = true } = options;
  const anonymousCurrency = usePreferencesStore((s) => s.currency);
  const userCurrency = (user as any)?.preferred_currency || anonymousCurrency;

  return useQuery<MultiCartResponse>({
    queryKey: [...MULTI_CART_QUERY_KEY, userCurrency],
    queryFn: () => getMultiCart(userCurrency),
    enabled: enabled && isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds (matches server cache TTL)
    refetchOnWindowFocus: true,
  });
}
